import React, { useState, useMemo, useCallback } from 'react'
import {
  Box,
  TextField,
  Typography,
  InputAdornment,
  CircularProgress,
  Chip,
  SvgIcon,
  Fade,
  useTheme,
} from '@mui/material'
import {
  Search as SearchIcon,
  AccessTime as RecentIcon,
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
} from '@mui/icons-material'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { getSearchableItems, SECTION_ORDER, type SearchItem } from './search.constants'
import { useRecentItems, formatRelativeTime } from './hooks/use-recent-items'
import { useArticleSearch } from './hooks/use-article-search'
import { searchStyles } from './search.styles'

// Section icons
import IconApps from '@hapana/shared/web/styles/assets/svgs/apps.svg'
import IconPeople from '@hapana/shared/web/styles/assets/svgs/people-group.svg'
import IconCreditCard from '@hapana/shared/web/styles/assets/svgs/credit-card-small.svg'
import IconCalendar from '@hapana/shared/web/styles/assets/svgs/calendar.svg'
import IconSetting from '@hapana/shared/web/styles/assets/svgs/setting.svg'
import IconShoppingCart from '@hapana/shared/web/styles/assets/svgs/shopping-cart.svg'
import IconFileGraph from '@hapana/shared/web/styles/assets/svgs/file-graph.svg'
import IconKey from '@hapana/shared/web/styles/assets/svgs/key-skeleton.svg'
import IconHelpDocs from '@hapana/shared/web/styles/assets/svgs/file-alt.svg'
import IconChartLine from '@hapana/shared/web/styles/assets/svgs/chart-line.svg'

const SECTION_ICON_MAP: Record<string, any> = {
  Features: IconApps,
  Reports: IconChartLine,
  Members: IconPeople,
  Payments: IconCreditCard,
  'Scheduler Settings': IconCalendar,
  'Location Settings': IconSetting,
  'Retail Settings': IconShoppingCart,
  'Account Settings': IconFileGraph,
  'Brand Management': IconApps,
  'User & Team Access': IconKey,
  Articles: IconHelpDocs,
}

const COLLAPSED_ITEM_COUNT = 5

function groupBySection(items: SearchItem[]): Record<string, SearchItem[]> {
  const groups: Record<string, SearchItem[]> = {}
  for (const item of items) {
    if (!groups[item.section]) {
      groups[item.section] = []
    }
    groups[item.section].push(item)
  }
  // Sort items alphabetically within each section
  for (const section of Object.keys(groups)) {
    groups[section].sort((a, b) => a.name.localeCompare(b.name))
  }
  return groups
}

function filterItems(items: SearchItem[], query: string): SearchItem[] {
  const lower = query.toLowerCase()
  return items.filter(
    (item) => item.name.toLowerCase().includes(lower) || item.keywords.toLowerCase().includes(lower)
  )
}

function SectionIcon({ section }: { section: string }) {
  const theme = useTheme()
  const Icon = SECTION_ICON_MAP[section]
  if (!Icon) return null
  return <SvgIcon component={Icon} inheritViewBox sx={searchStyles.sectionIcon(theme)} />
}

interface SearchProps {
  onItemClick?: () => void
  onClose?: () => void
}

export default function Search({ onItemClick, onClose }: SearchProps = {}) {
  const { t } = useTranslation('core')
  const theme = useTheme()
  const navigate = useNavigate()
  const { siteId } = useParams<{ siteId: string }>()

  const [searchQuery, setSearchQuery] = useState('')
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({})
  const { recentItems, addRecentItem } = useRecentItems()
  const { results: articleResults, isSearching } = useArticleSearch(searchQuery)

  const allItems = useMemo(() => getSearchableItems(siteId), [siteId])

  const displayItems = useMemo(() => {
    if (!searchQuery.trim()) return allItems
    return filterItems(allItems, searchQuery)
  }, [allItems, searchQuery])

  const groupedItems = useMemo(() => {
    const groups = groupBySection(displayItems)
    const sorted: Record<string, SearchItem[]> = {}
    for (const section of SECTION_ORDER) {
      if (groups[section]) {
        sorted[section] = groups[section]
      }
    }
    for (const [section, items] of Object.entries(groups)) {
      if (!sorted[section]) {
        sorted[section] = items
      }
    }
    return sorted
  }, [displayItems])

  const toggleSection = useCallback((section: string) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }))
  }, [])

  const handleFeatureClick = (item: SearchItem) => {
    addRecentItem({
      id: item.id,
      name: item.name,
      section: item.section,
      href: item.href,
    })
    navigate(item.href)
    onItemClick?.()
  }

  const handleArticleClick = (slug?: string, id?: string, title?: string) => {
    const articleId = slug || id
    if (!articleId) return
    const docsPath = siteId
      ? `/s/${siteId}/assistant/docs?article=${encodeURIComponent(articleId)}`
      : `/c/assistant/docs?article=${encodeURIComponent(articleId)}`
    addRecentItem({
      id: `article-${articleId}`,
      name: title || articleId.replace(/-/g, ' '),
      section: 'Articles',
      href: docsPath,
    })
    navigate(docsPath)
    onItemClick?.()
  }

  const handleRecentClick = (item: { href: string }) => {
    navigate(item.href)
    onItemClick?.()
  }

  const hasSearchQuery = searchQuery.trim().length > 0
  const hasResults =
    Object.keys(groupedItems).length > 0 || (hasSearchQuery && articleResults.length > 0)

  const rowSx = searchStyles.row(theme)
  const sectionHeaderSx = searchStyles.sectionHeader(theme)
  const moreBtnSx = searchStyles.moreButton(theme)

  return (
    <Box sx={searchStyles.root}>
      {/* Search Input — fixed at top */}
      <Box sx={searchStyles.searchInputWrapper}>
        <Box sx={searchStyles.searchInputRow}>
          <TextField
            fullWidth
            autoFocus
            size="small"
            placeholder={t('search.placeholder', 'Search features, settings, help articles...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={searchStyles.searchIcon(theme)} />
                </InputAdornment>
              ),
              endAdornment: isSearching ? (
                <InputAdornment position="end">
                  <CircularProgress size={16} />
                </InputAdornment>
              ) : null,
            }}
            sx={searchStyles.textField(theme)}
          />
          {onClose && (
            <Box onClick={onClose} sx={searchStyles.closeButton(theme)}>
              <CloseIcon sx={searchStyles.closeIcon} />
            </Box>
          )}
        </Box>
      </Box>

      {/* Scrollable Results */}
      <Box sx={searchStyles.scrollableResults}>
        {/* Recents Section */}
        {!hasSearchQuery && recentItems.length > 0 && (
          <Box sx={searchStyles.sectionWrapper}>
            <Box sx={sectionHeaderSx}>
              <RecentIcon sx={searchStyles.recentIcon(theme)} />
              <Typography component="span" sx={searchStyles.inheritTypography}>
                {t('search.recents', 'Recents')}
              </Typography>
            </Box>
            {recentItems.map((item) => (
              <Box key={item.id} onClick={() => handleRecentClick(item)} sx={rowSx}>
                <Typography variant="body2" sx={searchStyles.rowItemName}>
                  {item.name}
                </Typography>
                <Typography variant="caption" sx={searchStyles.rowSectionLabel(theme)}>
                  {item.section}
                </Typography>
                <Typography variant="caption" sx={searchStyles.recentTimestamp(theme)}>
                  {formatRelativeTime(item.timestamp, t)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Feature Sections */}
        {Object.entries(groupedItems).map(([section, items]) => {
          const isExpanded = expandedSections[section] || hasSearchQuery
          const visibleItems = isExpanded ? items : items.slice(0, COLLAPSED_ITEM_COUNT)
          const hasMore = items.length > COLLAPSED_ITEM_COUNT
          const hiddenCount = items.length - COLLAPSED_ITEM_COUNT

          return (
            <Box key={section} sx={searchStyles.sectionWrapper}>
              <Box sx={sectionHeaderSx}>
                <SectionIcon section={section} />
                <Typography component="span" sx={searchStyles.inheritTypography}>
                  {section}
                </Typography>
                {!hasSearchQuery && hasMore && (
                  <Typography component="span" sx={searchStyles.sectionCount(theme)}>
                    ({items.length})
                  </Typography>
                )}
              </Box>
              {visibleItems.map((item) => (
                <Box key={item.id} onClick={() => handleFeatureClick(item)} sx={rowSx}>
                  <Typography variant="body2" sx={searchStyles.rowItemName}>
                    {item.name}
                  </Typography>
                  <Typography variant="caption" sx={searchStyles.rowSectionLabel(theme)}>
                    {item.section}
                  </Typography>
                </Box>
              ))}
              {!hasSearchQuery && hasMore && !isExpanded && (
                <Box onClick={() => toggleSection(section)} sx={moreBtnSx}>
                  <Typography variant="body2" sx={searchStyles.moreButtonText(theme)}>
                    {t('search.showMore', 'Show {{count}} more', { count: hiddenCount })}
                  </Typography>
                  <ExpandMoreIcon sx={searchStyles.expandMoreIcon(theme)} />
                </Box>
              )}
            </Box>
          )
        })}

        {/* Article Results — below features, fade in */}
        {hasSearchQuery && (
          <Fade in={articleResults.length > 0} timeout={300}>
            <Box sx={searchStyles.sectionWrapper}>
              {articleResults.length > 0 && (
                <>
                  <Box sx={sectionHeaderSx}>
                    <SectionIcon section="Articles" />
                    <Typography component="span" sx={searchStyles.inheritTypography}>
                      {t('search.articles', 'Articles')}
                    </Typography>
                  </Box>
                  {articleResults.map((article) => (
                    <Box
                      key={article.id}
                      onClick={() => handleArticleClick(article.slug, article.id, article.title)}
                      sx={rowSx}
                    >
                      <Typography variant="body2" sx={searchStyles.rowItemName}>
                        {article.title}
                      </Typography>
                      {article.category && (
                        <Chip label={article.category} size="small" sx={searchStyles.articleChip} />
                      )}
                    </Box>
                  ))}
                </>
              )}
            </Box>
          </Fade>
        )}

        {/* No results */}
        {hasSearchQuery && !hasResults && !isSearching && (
          <Box sx={searchStyles.noResults}>
            <Typography variant="body2" color="text.secondary" sx={searchStyles.noResultsText}>
              {t('search.noResults', 'No results found for "{{query}}"', { query: searchQuery })}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  )
}
